import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { awsConfig } from "../aws-config";
import { createPaymentIntent } from "../api";

const stripePromise = loadStripe(awsConfig.stripePublishableKey);

interface Lead {
  bookingId: string;
  createdAt: string;
  serviceType: string;
  address: string;
  scheduledDate: string;
  clientId: string;
  jobSize?: string;
}

interface PaymentModalProps {
  lead: Lead;
  category: string;
  onSuccess: () => void;
  onClose: () => void;
}

function CheckoutForm({ lead, category, onSuccess, onClose }: PaymentModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentData, setPaymentData] = useState<any>(null);
  const [initLoading, setInitLoading] = useState(true);

  useEffect(() => {
    async function init() {
      try {
        const data = await createPaymentIntent(
          lead.bookingId,
          lead.createdAt,
          category,
          lead.jobSize || "Medium"
        );
        setPaymentData(data);
      } catch (err: any) {
        setError("Failed to load payment details");
      } finally {
        setInitLoading(false);
      }
    }
    init();
  }, []);

  async function handleSubmit() {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");

    const card = elements.getElement(CardElement);
    if (!card) return;

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      paymentData.clientSecret,
      { payment_method: { card } }
    );

    if (stripeError) {
      setError(stripeError.message || "Payment failed");
      setLoading(false);
    } else if (paymentIntent?.status === "succeeded") {
      onSuccess();
    }
  }

  if (initLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Init failed: never fall through to the paid-form branch
  if (!paymentData) {
    return (
      <div className="text-center py-6">
        <div className="text-3xl mb-3">⚠️</div>
        <div className="font-bold text-navy mb-2">Couldn't load payment details</div>
        <div className="text-sm text-gray-500 mb-4">{error || "Please try again in a moment."}</div>
        <button onClick={onClose} className="w-full bg-gray-100 text-navy rounded-full py-3 font-bold">
          Close
        </button>
      </div>
    );
  }

  if (paymentData?.free) {
    return (
      <div className="text-center py-4">
        <div className="text-4xl mb-3">🎉</div>
        <div className="font-bold text-navy text-lg mb-2">Free Trial Lead!</div>
        <div className="text-sm text-gray-500 mb-4">
          {paymentData.message}
        </div>
        <button
          onClick={onSuccess}
          className="w-full bg-mouna-blue text-white rounded-full py-3 font-bold"
        >
          Accept Lead for Free
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-blue-pale rounded-xl p-4 mb-4">
        <div className="text-xs text-gray-500 mb-1">Lead fee for</div>
        <div className="font-bold text-navy">{lead.serviceType}</div>
        <div className="text-2xl font-bold text-mouna-blue mt-1">
          ${(paymentData?.amount / 100).toFixed(2)}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 text-sm rounded-xl p-3 mb-4">
          {error}
        </div>
      )}

      <div className="border border-gray-200 rounded-xl p-4 mb-4">
        <CardElement options={{
          style: {
            base: {
              fontSize: "16px",
              color: "#0D1B3E",
              "::placeholder": { color: "#9CA3AF" }
            }
          }
        }} />
      </div>

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-500 rounded-full py-3 font-semibold text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || !stripe}
          className="flex-1 bg-mouna-blue text-white rounded-full py-3 font-bold text-sm"
        >
          {loading ? "Processing..." : `Pay $${(paymentData?.amount / 100).toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

export default function PaymentModal({ lead, category, onSuccess, onClose }: PaymentModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white rounded-t-3xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-navy text-lg">Accept Lead</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <Elements stripe={stripePromise}>
          <CheckoutForm
            lead={lead}
            category={category}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </Elements>
      </div>
    </div>
  );
}
